import React, { useEffect, useId, useRef, useState } from 'react';
import { FaCheck, FaChevronDown, FaSearch, FaTimes } from 'react-icons/fa';
import { apiRequest } from '../utils/auth';
import './RecipientAutocomplete.css';

export interface RecipientOption { userId: string; name: string; email?: string; avatar?: string }
interface Props { value: RecipientOption[]; onChange: (users: RecipientOption[]) => void }

export default function RecipientAutocomplete({ value, onChange }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<RecipientOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [active, setActive] = useState(-1);
  const [retry, setRetry] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const options = results.filter(user => !value.some(selected => selected.userId === user.userId));

  useEffect(() => {
    const controller = new AbortController();
    let current = true;
    setResults([]); setActive(-1); setError('');
    if (query.trim().length < 2) { setLoading(false); return () => { current = false; controller.abort(); }; }
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const response = await apiRequest<{ success: boolean; data: RecipientOption[] }>(`/api/admin/notifications/search-users?q=${encodeURIComponent(query.trim())}`, { signal: controller.signal });
        if (!current) return;
        if (!response.success || !Array.isArray(response.data)) throw new Error('Search unavailable');
        setResults(response.data.map(user => ({ ...user, name: user.name || user.email || user.userId })));
      } catch {
        if (current) setError('Unable to load users.');
      } finally { if (current) setLoading(false); }
    }, 300);
    return () => { current = false; controller.abort(); window.clearTimeout(timer); };
  }, [query, retry]);

  useEffect(() => {
    const close = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, []);
  useEffect(() => {
    root.current?.querySelector('[data-active="true"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const choose = (user: RecipientOption) => {
    if (value.some(selected => selected.userId === user.userId)) return;
    onChange([...value, user]); setQuery(''); setResults([]); setActive(-1); setOpen(true); input.current?.focus();
  };
  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) return;
    if (event.key === 'Escape' && open) { event.preventDefault(); event.stopPropagation(); setOpen(false); setActive(-1); }
    else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault(); setOpen(true);
      if (options.length) setActive(previous => event.key === 'ArrowDown' ? (previous + 1) % options.length : previous <= 0 ? options.length - 1 : previous - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault(); // Typed text is never a recipient or a form submit.
      if (open && active >= 0 && options[active]) choose(options[active]);
    } else if (event.key === 'Backspace' && !query && value.length) onChange(value.slice(0, -1));
  };
  const activeId = open && options[active] ? `${listId}-${active}` : undefined;
  return <div className="recipient-select" ref={root} onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
  }}>
    <div className="recipient-select__control" onClick={() => input.current?.focus()}>
      <FaSearch className="recipient-select__search" aria-hidden="true" />
      <div className="recipient-select__values">
        {value.map(user => <span className="recipient-select__chip" key={user.userId} title={user.email || user.userId}>
          <span>{user.name}</span><button type="button" aria-label={`Remove ${user.name}`} onClick={event => { event.stopPropagation(); onChange(value.filter(selected => selected.userId !== user.userId)); input.current?.focus(); }}><FaTimes aria-hidden="true" /></button>
        </span>)}
        <input ref={input} role="combobox" aria-label="Search recipients by name, email or user ID" aria-autocomplete="list" aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? listId : undefined} aria-activedescendant={activeId} autoComplete="off" maxLength={200}
          placeholder={value.length ? 'Add another user…' : 'Search users to add…'} value={query}
          onFocus={() => setOpen(true)} onChange={event => { setQuery(event.target.value); setOpen(true); setActive(-1); }} onKeyDown={onKeyDown} />
      </div>
      <button type="button" className="recipient-select__toggle" aria-label={open ? 'Close user suggestions' : 'Open user suggestions'} onClick={event => { event.stopPropagation(); if (open) setOpen(false); else { input.current?.focus(); setOpen(true); } }}><FaChevronDown aria-hidden="true" /></button>
    </div>
    {open && <div className="recipient-select__popup">
      <div className="recipient-select__status" role="status">
        {loading ? 'Searching…' : error ? <>{error} <button type="button" onClick={() => setRetry(n => n + 1)}>Retry</button></> : query.trim().length < 2 ? 'Type 2+ characters to search.' : options.length ? `${options.length} users found` : results.length ? 'All matches already selected.' : 'No matching users.'}
      </div>
      <ul id={listId} role="listbox" aria-label="User suggestions" aria-multiselectable="true" aria-busy={loading}>
        {options.map((user, index) => <li id={`${listId}-${index}`} key={user.userId} role="option" aria-selected={false} data-active={active === index} className={active === index ? 'is-active' : ''}
          onMouseDown={event => event.preventDefault()} onMouseEnter={() => setActive(index)} onClick={() => choose(user)}>
          {user.avatar ? <img src={user.avatar} alt="" /> : <span className="recipient-select__avatar" aria-hidden="true">{user.name.slice(0, 1).toUpperCase()}</span>}
          <span className="recipient-select__identity"><strong>{user.name}</strong><small>{user.email || user.userId}</small></span>
          <FaCheck className="recipient-select__option-check" aria-hidden="true" />
        </li>)}
      </ul>
    </div>}
    <span className="recipient-select__count" role="status">{value.length ? `${value.length} selected` : 'Search by name, email or user ID'}</span>
  </div>;
}
